'use client';

import { useMemo, useState } from 'react';

import { US_STATES } from '@web/constants';
import { Divider, SmartInput, ToggleSwitch } from '@web/ui';
import { buildStateOptions, isUnitedStatesCountry } from '@web/utils';

import styles from './ContactAddressFields.module.scss';

import type { AddressInformation } from '@/features/contacts/types';
import type { ReactNode } from 'react';

type ContactAddressFieldsProps = {
  title: string;
  initialAddress?: AddressInformation | null;
  errors?: Record<string, string | undefined>;
  showDivider?: boolean;
  children?: ReactNode;
};

function isInternationalAddress(address?: AddressInformation | null) {
  const country = address?.country?.trim();
  return Boolean(country) && !isUnitedStatesCountry(country);
}

export function ContactAddressFields({
  title,
  initialAddress,
  errors = {},
  showDivider = false,
  children,
}: ContactAddressFieldsProps) {
  const [isInternational, setIsInternational] = useState(() =>
    isInternationalAddress(initialAddress),
  );
  const [addressLine1, setAddressLine1] = useState(
    initialAddress?.address_line_1 ?? '',
  );
  const [addressLine2, setAddressLine2] = useState(
    initialAddress?.address_line_2 ?? '',
  );
  const [city, setCity] = useState(initialAddress?.city ?? '');
  const [zipCode, setZipCode] = useState(initialAddress?.zip_code ?? '');
  const [country, setCountry] = useState(() =>
    isInternationalAddress(initialAddress)
      ? (initialAddress?.country ?? '')
      : '',
  );
  const [internationalRegion, setInternationalRegion] = useState(() =>
    isInternationalAddress(initialAddress) ? (initialAddress?.state ?? '') : '',
  );
  const [usState, setUsState] = useState(() =>
    isInternationalAddress(initialAddress)
      ? ''
      : (buildStateOptions(US_STATES, initialAddress?.state).defaultValue ??
        ''),
  );

  const stateOptions = useMemo(
    () => [
      { value: '', label: 'Select' },
      ...buildStateOptions(US_STATES, usState).options,
    ],
    [usState],
  );

  return (
    <section className={styles.section} aria-label={`${title} section`}>
      <div className={styles.header}>
        <h2 className={styles.title}>{title}</h2>
        <div className={styles.modeSwitch}>
          <span>US</span>
          <ToggleSwitch
            checked={isInternational}
            ariaLabel="Use international address"
            onChange={setIsInternational}
          />
          <span>Intl</span>
        </div>
      </div>

      {showDivider ? <Divider /> : null}
      {children}
      <input
        type="hidden"
        name="address_mode"
        value={isInternational ? 'international' : 'us'}
      />

      <div className={styles.oneCol}>
        <SmartInput
          type="text"
          name="address_line_1"
          label={isInternational ? 'Address Line 1' : 'Address 1'}
          value={addressLine1}
          onChange={setAddressLine1}
          error={errors['address_information.address_line_1']}
        />

        <SmartInput
          type="text"
          name="address_line_2"
          label={isInternational ? 'Address Line 2' : 'Address 2'}
          value={addressLine2}
          onChange={setAddressLine2}
          error={errors['address_information.address_line_2']}
        />
      </div>

      <div className={styles.twoCol}>
        <SmartInput
          type="text"
          name="city"
          label={isInternational ? 'City / Town' : 'City'}
          value={city}
          onChange={setCity}
          error={errors['address_information.city']}
        />

        {isInternational ? (
          <SmartInput
            type="text"
            name="state"
            label="Region / Province"
            value={internationalRegion}
            onChange={setInternationalRegion}
            error={errors['address_information.state']}
          />
        ) : (
          <SmartInput
            type="select"
            name="state"
            label="State"
            value={usState}
            onChange={setUsState}
            options={stateOptions}
            error={errors['address_information.state']}
          />
        )}

        <SmartInput
          type="text"
          name="zip_code"
          label={isInternational ? 'Postal Code' : 'ZIP Code'}
          value={zipCode}
          onChange={setZipCode}
          error={errors['address_information.zip_code']}
        />

        {isInternational ? (
          <SmartInput
            type="text"
            name="country"
            label="Country"
            value={country}
            onChange={setCountry}
            error={errors['address_information.country']}
          />
        ) : (
          <input type="hidden" name="country" value="US" />
        )}
      </div>
    </section>
  );
}
